import { devLog, devError } from '../utils/devLogger';
import { collection, getDocs, doc, getDoc, updateDoc, addDoc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORIES_BY_ID,
  DOCUMENT_CATEGORIES_BY_NAME,
  LEARNER_DOCUMENT_CATEGORY_ORDER,
  DOCUMENT_DEADLINES,
  checkDocumentDeadline,
  getConditionalMandatoryStatus,
  validateDocumentAge
} from '../config/documentCategories';
import { toCategoryId } from '../constants/documentCategories';

class DocumentDeadlineService {
  resolveRequiredCategoryKey(rawValue) {
    if (!rawValue || typeof rawValue !== 'string') {
      return null;
    }

    if (DOCUMENT_CATEGORIES[rawValue]) {
      return rawValue;
    }

    if (DOCUMENT_CATEGORIES_BY_ID[rawValue]) {
      return DOCUMENT_CATEGORIES_BY_ID[rawValue].key;
    }

    if (DOCUMENT_CATEGORIES_BY_NAME[rawValue]) {
      return DOCUMENT_CATEGORIES_BY_NAME[rawValue].key;
    }

    const normalized = String(rawValue).toUpperCase();
    if (DOCUMENT_CATEGORIES[normalized]) {
      return normalized;
    }

    return null;
  }
  
  async createDocumentDeadlines(userId, registrationDate) {
    try {
      const deadlineDate = new Date(registrationDate);
      deadlineDate.setDate(deadlineDate.getDate() + DOCUMENT_DEADLINES.INITIAL_UPLOAD);
      
      const deadlineDoc = {
        userId,
        registrationDate,
        documentDeadline: deadlineDate,
        status: 'active',
        remindersSet: false,
        escalationLevel: 0,
        createdAt: serverTimestamp()
      };
      
      await addDoc(collection(db, 'document_deadlines'), deadlineDoc);
      
      return {
        success: true,
        deadlineDate: deadlineDate
      };
    } catch (error) {
      devError('Error creating document deadlines:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  async getUserDocumentStatus(userId) {
    try {
      const toDateSafe = (value) => {
        if (!value) return null;
        if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
        if (typeof value.toDate === 'function') {
          const converted = value.toDate();
          return Number.isNaN(converted.getTime()) ? null : converted;
        }
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      };

      const normalizeStatus = (data = {}) => {
        if (data.reviewStatus === 'qa_approved') return 'verified';
        if (data.reviewStatus === 'qa_rejected') return 'rejected';
        if (data.status === 'verified' || data.status === 'approved') return 'verified';
        if (data.status === 'rejected' || data.status === 'missing') return 'rejected';
        return data.status || 'pending';
      };

      const resolveCategoryId = (data = {}) =>
        data.categoryId ||
        data.categoryKey ||
        (data.category ? toCategoryId(data.category) : null);

      const getRecordRank = (record) => {
        const reviewStatus = String(record.reviewStatus || '').toLowerCase();
        const status = String(record.status || '').toLowerCase();
        const isReviewed =
          reviewStatus === 'qa_approved' ||
          reviewStatus === 'qa_rejected' ||
          status === 'verified' ||
          status === 'approved' ||
          status === 'rejected';
        const reviewedAt = toDateSafe(record.reviewedAt);
        const uploadedAt = toDateSafe(record.uploadedAt);
        const activityTime = (reviewedAt || uploadedAt || new Date(0)).getTime();
        return {
          isReviewed,
          activityTime
        };
      };

      const documentsQuery = query(
        collection(db, 'users', userId, 'documents'),
        orderBy('uploadedAt', 'desc')
      );

      const documentsSnapshot = await getDocs(documentsQuery);
      const userDocumentsById = {};
      const userDocumentsList = [];

      const candidateDocuments = [];

      documentsSnapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        const categoryId = resolveCategoryId(data);
        if (!categoryId) {
          return;
        }

        const documentRecord = {
          id: docSnapshot.id,
          ...data,
          status: normalizeStatus(data),
          categoryId,
          source: 'user_subcollection'
        };
        if (data.deleted === true) return;
        candidateDocuments.push(documentRecord);
      });

      // Merge in central documents so learner view reflects QA review state even
      // when legacy mirrors lag behind.
      try {
        const centralDocsQuery = query(
          collection(db, 'documents'),
          where('userId', '==', userId)
        );
        const centralDocsSnapshot = await getDocs(centralDocsQuery);
        centralDocsSnapshot.docs.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          const categoryId = resolveCategoryId(data);
          if (!categoryId || data.deleted === true) return;
          candidateDocuments.push({
            id: docSnapshot.id,
            ...data,
            status: normalizeStatus(data),
            categoryId,
            source: 'central_documents'
          });
        });
      } catch (centralDocError) {
        devLog('DocumentDeadlineService: central documents lookup skipped', centralDocError);
      }

      candidateDocuments.forEach((documentRecord) => {
        const categoryId = documentRecord.categoryId;
        const existing = userDocumentsById[categoryId];
        if (!existing) {
          userDocumentsById[categoryId] = documentRecord;
        } else {
          const existingRank = getRecordRank(existing);
          const nextRank = getRecordRank(documentRecord);
          if (
            nextRank.activityTime > existingRank.activityTime ||
            (nextRank.activityTime === existingRank.activityTime &&
              nextRank.isReviewed &&
              !existingRank.isReviewed)
          ) {
            userDocumentsById[categoryId] = documentRecord;
          }
        }
      });

      userDocumentsList.push(...Object.values(userDocumentsById));

      const learnerProfileDoc = await getDoc(doc(db, 'users', userId));
      const userProfile = learnerProfileDoc.exists() ? learnerProfileDoc.data() : {};
      const registrationDate =
        userProfile?.createdAt && typeof userProfile.createdAt.toDate === 'function'
          ? userProfile.createdAt.toDate()
          : new Date();

      const deadlineInfo = checkDocumentDeadline(registrationDate);

      const programRequiredDocumentKeys = Array.isArray(userProfile?.requiredDocuments)
        ? userProfile.requiredDocuments.map((value) => this.resolveRequiredCategoryKey(value)).filter(Boolean)
        : [];
      const fallbackProgramKeys = Array.isArray(userProfile?.programRequiredDocuments)
        ? userProfile.programRequiredDocuments.map((value) => this.resolveRequiredCategoryKey(value)).filter(Boolean)
        : [];
      let requiredDocumentKeys = Array.from(new Set([
        ...programRequiredDocumentKeys,
        ...fallbackProgramKeys
      ]));

      if (requiredDocumentKeys.length === 0 && userProfile?.programId) {
        try {
          const programDoc = await getDoc(doc(db, 'programs', userProfile.programId));
          if (programDoc.exists()) {
            const programData = programDoc.data() || {};
            requiredDocumentKeys = Array.from(
              new Set(
                (Array.isArray(programData.requiredDocuments) ? programData.requiredDocuments : [])
                  .map((value) => this.resolveRequiredCategoryKey(value))
                  .filter(Boolean)
              )
            );
          }
        } catch (programError) {
          devLog('DocumentDeadlineService: failed to load program requirements', programError);
        }
      }

      if (requiredDocumentKeys.length === 0) {
        requiredDocumentKeys = LEARNER_DOCUMENT_CATEGORY_ORDER;
      }

      const documentStatus = {};
      const missingDocuments = [];
      const rejectedDocuments = [];
      const pendingDocuments = [];
      const staleDocuments = [];

      requiredDocumentKeys.forEach((legacyKey) => {
        const categoryConfig = DOCUMENT_CATEGORIES[legacyKey];
        if (!categoryConfig) return; // Skip if category not found

        const userDocument =
          userDocumentsById[categoryConfig.id] ||
          userDocumentsById[toCategoryId(categoryConfig.name)] ||
          null;

        const mandatoryForUser = getConditionalMandatoryStatus(categoryConfig.id, userProfile);
        const recency = userDocument
          ? validateDocumentAge(categoryConfig.id, userDocument.certifiedAt)
          : { valid: !categoryConfig.recencyRequired };

        if (!userDocument && mandatoryForUser) {
          missingDocuments.push({
            category: categoryConfig.id,
            info: categoryConfig,
            urgent: deadlineInfo.isOverdue || deadlineInfo.isUrgent
          });
        }

        if (userDocument) {
          if (userDocument.status === 'rejected') {
            rejectedDocuments.push({
              category: categoryConfig.id,
              info: categoryConfig,
              document: userDocument,
              urgent: true
            });
          } else if (userDocument.status === 'pending') {
            pendingDocuments.push({
              category: categoryConfig.id,
              info: categoryConfig,
              document: userDocument,
              urgent: false
            });
          }

          if (!recency.valid) {
            staleDocuments.push({
              category: categoryConfig.id,
              info: categoryConfig,
              document: userDocument,
              recency
            });
          }
        }

        const statusEntry = {
          category: categoryConfig,
          document: userDocument,
          status: userDocument ? userDocument.status || 'pending' : 'missing',
          mandatory: mandatoryForUser,
          recency,
          recencyValid: recency.valid
        };

        documentStatus[categoryConfig.id] = statusEntry;
        documentStatus[legacyKey] = statusEntry;
        documentStatus[categoryConfig.name] = statusEntry;
      });

      return {
        success: true,
        data: {
          userId,
          registrationDate,
          deadlineInfo,
          documentStatus,
          documents: userDocumentsList,
          summary: {
            total: requiredDocumentKeys.length,
            missing: missingDocuments.length,
            pending: pendingDocuments.length,
            rejected: rejectedDocuments.length,
            stale: staleDocuments.length,
            verified: userDocumentsList.filter((doc) => doc.status === 'verified').length
          },
          alerts: {
            missing: missingDocuments,
            rejected: rejectedDocuments,
            pending: pendingDocuments,
            stale: staleDocuments
          },
          profile: userProfile
        }
      };
    } catch (error) {
      devError('Error getting document status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  async getOverdueUsers() {
    try {
      // Get all active users
      const usersQuery = query(
        collection(db, 'users'),
        where('isActive', '==', true),
        where('role', '==', 'learner')
      );
      
      const usersSnapshot = await getDocs(usersQuery);
      const overdueUsers = [];
      
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        const registrationDate = userData.createdAt?.toDate();
        
        if (registrationDate) {
          const deadlineInfo = checkDocumentDeadline(registrationDate);
          
          if (deadlineInfo.isOverdue) {
            const statusResult = await this.getUserDocumentStatus(userData.uid);
            
            if (statusResult.success && statusResult.data.summary.missing > 0) {
              overdueUsers.push({
                userId: userData.uid,
                userInfo: userData,
                registrationDate,
                deadlineInfo,
                missingCount: statusResult.data.summary.missing,
                documents: statusResult.data.alerts.missing
              });
            }
          }
        }
      }
      
      return {
        success: true,
        overdueUsers: overdueUsers.sort((a, b) => a.deadlineInfo.daysUntilDeadline - b.deadlineInfo.daysUntilDeadline)
      };
    } catch (error) {
      devError('Error getting overdue users:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  async sendDocumentReminders() {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('isActive', '==', true),
        where('role', '==', 'learner')
      );
      
      const usersSnapshot = await getDocs(usersQuery);
      const remindersToSend = [];
      
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        const registrationDate = userData.createdAt?.toDate();
        
        if (registrationDate) {
          const deadlineInfo = checkDocumentDeadline(registrationDate);
          
          if (deadlineInfo.needsReminder || deadlineInfo.isOverdue) {
            const statusResult = await this.getUserDocumentStatus(userData.uid);
            
            if (statusResult.success && 
                (statusResult.data.summary.missing > 0 || statusResult.data.summary.rejected > 0)) {
              
              remindersToSend.push({
                userId: userData.uid,
                email: userData.email,
                displayName: userData.displayName,
                deadlineInfo,
                missingDocuments: statusResult.data.alerts.missing,
                rejectedDocuments: statusResult.data.alerts.rejected
              });
            }
          }
        }
      }
      
      // In a real implementation, you would send emails here
      devLog(`Would send ${remindersToSend.length} document reminder emails`);
      
      return {
        success: true,
        remindersSent: remindersToSend.length,
        details: remindersToSend
      };
    } catch (error) {
      devError('Error sending document reminders:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  async updateDocumentDeadline(userId, newDeadline, reason) {
    try {
      const deadlineQuery = query(
        collection(db, 'document_deadlines'),
        where('userId', '==', userId),
        where('status', '==', 'active')
      );
      
      const snapshot = await getDocs(deadlineQuery);
      
      if (snapshot.empty) {
        return {
          success: false,
          error: 'No active deadline found for user'
        };
      }
      
      const deadlineDoc = snapshot.docs[0];
      await updateDoc(deadlineDoc.ref, {
        documentDeadline: new Date(newDeadline),
        extensionReason: reason,
        modifiedAt: serverTimestamp(),
        originalDeadline: deadlineDoc.data().documentDeadline
      });
      
      return {
        success: true,
        message: 'Document deadline updated successfully'
      };
    } catch (error) {
      devError('Error updating document deadline:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  async generateDocumentReport(cohortId = null, programId = null) {
    try {
      let usersQuery = query(collection(db, 'users'), where('role', '==', 'learner'));
      
      if (cohortId) {
        usersQuery = query(usersQuery, where('cohortId', '==', cohortId));
      }
      if (programId) {
        usersQuery = query(usersQuery, where('programId', '==', programId));
      }
      
      const usersSnapshot = await getDocs(usersQuery);
      const reportData = [];
      
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        const statusResult = await this.getUserDocumentStatus(userData.uid);
        
        if (statusResult.success) {
          reportData.push({
            userId: userData.uid,
            displayName: userData.displayName,
            email: userData.email,
            registrationDate: userData.createdAt?.toDate(),
            deadlineInfo: statusResult.data.deadlineInfo,
            summary: statusResult.data.summary,
            completionPercentage: Math.round(
              (statusResult.data.summary.verified / statusResult.data.summary.total) * 100
            )
          });
        }
      }
      
      return {
        success: true,
        reportData: reportData.sort((a, b) => a.completionPercentage - b.completionPercentage),
        summary: {
          totalLearners: reportData.length,
          averageCompletion: Math.round(
            reportData.reduce((acc, user) => acc + user.completionPercentage, 0) / reportData.length
          ),
          overdueCount: reportData.filter(user => user.deadlineInfo.isOverdue).length,
          completeCount: reportData.filter(user => user.completionPercentage === 100).length
        }
      };
    } catch (error) {
      devError('Error generating document report:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

const documentDeadlineService = new DocumentDeadlineService();
export default documentDeadlineService;
